import React, { useEffect, useState } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import AppInstallBanner from '../../../../components/AppInstallBanner/AppInstallBanner';
import './CarePlusOrderDetail.css';
import * as translator from '../../../../utils/translator';
import * as http from '../../../../utils/http';
import { myAccountTimelineCheckpointsCreator } from '../../../../components/Timeline/TimelineProcessor';
import Timeline from '../../../../components/Timeline/Timeline';
import MyAccountOrderDetailRow from '../MyAccountOrderDetailRow/MyAccountOrderDetailRow';
import { toLocaleDateString } from '../../../../utils/helpers';
import CarePlusDeliveryHistory from './CarePlusDeliveryHistory';
import CarePlusNotificationBanner from '../CarePlus/CarePlusNotificationBanner/CarePlusNotificationBanner';
import CarePlusPauseModal from '../CarePlus/CarePlusPauseModal/CarePlusPauseModal';
import CarePlusCancelModal from '../CarePlus/CarePlusCancelModal/CarePlusCancelModal';
import SubscriptionCancellationSheet from '../CarePlus/SubscriptionCancellationSheet/SubscriptionCancellationSheet';
import { OrderData, OrderDetailsContext } from '../MyAccountOrderDetails/MyAccountOrderDetails';
import { EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS, NEED_CUSTOMER_ANSWER, ALTERNATIVE_TREATMENT_OFFERED } from '../../../../utils/consts';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import MyAccountOrderDisclaimer from '../MyAccountOrderDisclaimer/MyAccountOrderDisclaimer';
import { calculateColorAndDescription } from '../MyAccountOrderDisclaimer/MyAccountOrderDisclaimerPreProcess';
import PharmacySelection from '../MyAccountOrderDetails/PharmacySelection/PharmacySelection';
import CarePlusSubscriptionSummary from './CarePlusSubscriptionSummary';
import CarePlusChangePaymentForm from '../CarePlus/CarePlusStripePaymentMethod/CarePlusChangePaymentForm';
import CarePlusStripePaymentMethod from '../CarePlus/CarePlusStripePaymentMethod/CarePlusStripePaymentMethod';
import CarePlusSupport from './CarePlusSupport';
import MyAccountOrderNotes from '../MyAccountOrderNotes';
import MyAccountPendingActions from '../MyAccountPendingActions/MyAccountPendingActions';
import { isPostCheckoutVerificationCountry } from '../../../../utils/postCheckoutVerification';
import PauseIcon from '../../../../icons/careplus-action-pause.svg';
import CancelIcon from '../../../../icons/careplus-action-cancel.svg';

// WL DE cancellation flow (persona A): eligibility and any open (deferred) cancellation request.
interface CancellationFlowState {
  eligible: boolean;
  request: { id: number; type: 'cancel' | 'one_time_order'; reason: string; cancelAt: string } | null;
  offer: { available: boolean };
  progress?: { week: number };
  pause?: { minDate: string; maxDate: string } | null;
  persona?: 'A' | 'B';
  paused?: boolean;
  nextChargeDate?: string | null;
}

const CarePlusOrderDetail: React.FC = () => {
  const { order_hash_id: orderHashID } = useParams<{ order_hash_id: string }>();
  const { country, language, setLoading, setError, setActiveMenuLinkId, setStickyNotificationDetails, setRefreshData, refreshData, asideBarData, setCurrentGlobalModalName } = useOutletContext<any>();
  const subscriptionHashID = asideBarData?.carePlusSubscriptionHashId;
  const navigate = useNavigate();

  const [detail, setDetail] = useState<CarePlusSubscriptionDetail | null>(null);
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  // undefined until the order-details response answers, which keeps W5 hidden
  const [appInstallPlacements, setAppInstallPlacements] = useState<string[] | undefined>(undefined);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [cancellationFlow, setCancellationFlow] = useState<CancellationFlowState | null>(null);
  const [showCancellationSheet, setShowCancellationSheet] = useState(false);
  // 'pause' when the page's Pause button opened the sheet straight on the pause-until-date step
  const [sheetStartStep, setSheetStartStep] = useState<'menu' | 'pause'>('menu');
  const [withdrawing, setWithdrawing] = useState(false);

  const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };
  const locale = translator.getLocaleByCountry(country);

  useEffect(() => {
    setActiveMenuLinkId('my-orders');
    window.scrollTo(0, 0);
    setLoading(true);

    const detailUrl = orderHashID
      ? `${country}/account/care-plus-subscription/${subscriptionHashID}/details?orderHashID=${orderHashID}`
      : `${country}/account/care-plus-subscription/${subscriptionHashID}/details`;
    http.getFromServer(detailUrl, [
      http.MIDDLEWARES.CUSTOMER_ID,
      http.MIDDLEWARES.CART_ORDER,
    ])
      .then((res: any) => {
        const data: CarePlusSubscriptionDetail = res.data;
        setDetail(data);
        if (data.currentDelivery?.hashId) {
          return http.getFromServer(`${country}/account/orders/${data.currentDelivery.hashId}/details`, [
            http.MIDDLEWARES.CUSTOMER_ID,
          ]);
        }
        return null;
      })
      .then((orderRes: any) => {
        if (orderRes) {
          setOrderData(orderRes.data?.orderData ?? null);
          setAppInstallPlacements(orderRes.data?.appInstallPlacements);
        }
      })
      .catch((err) => { window.clientLogger.error('Error in CarePlusOrderDetail: ', err); setError(404); })
      .finally(() => setLoading(false));
  }, [subscriptionHashID, orderHashID, refreshData]);

  useEffect(() => {
    if (!subscriptionHashID) return;
    // Failure keeps today's behaviour (old modals), so errors are only logged.
    http.getFromServer(`${country}/account/care-plus-subscription/${subscriptionHashID}/cancellation-flow`, [http.MIDDLEWARES.CUSTOMER_ID])
      .then((res: any) => setCancellationFlow(res.data))
      .catch((err) => { window.clientLogger.error('Error loading cancellation flow state: ', err); setCancellationFlow(null); });
  }, [subscriptionHashID, refreshData]);

  if (!detail) return null;

  const { subscription, notifications, currentDelivery, deliveryHistory } = detail;
  const checkpoints = orderData ? myAccountTimelineCheckpointsCreator(orderData, language) : [];
  const hasPaymentFailed = notifications.some((n) => n.type === 'payment_failed');

  const t = (path: string) => translator.translate(language, 'my_account', path);

  const bannersToRender = notifications;
  const pendingCancellation = cancellationFlow?.request ?? null;
  const useNewCancellationFlow = Boolean(cancellationFlow?.eligible);

  const withdrawCancellationRequest = () => {
    setWithdrawing(true);
    http.postToServer('account-update/subscription/care-plus/cancellation-flow/withdraw', { subscriptionID: subscription.id }, [
      http.MIDDLEWARES.CUSTOMER_ID,
      http.MIDDLEWARES.CART_ORDER,
    ])
      .then(() => {
        setStickyNotificationDetails({
          show: true,
          level: 'success',
          title: t('CancellationFlow/pending/withdrawnTitle'),
          content: t('CancellationFlow/pending/withdrawnBody'),
        });
        setRefreshData((p: boolean) => !p);
      })
      .catch(() => setStickyNotificationDetails({ show: true, level: 'error', title: t('CancellationFlow/errors/title'), content: t('CancellationFlow/errors/body') }))
      .finally(() => setWithdrawing(false));
  };

  return (
    <OrderDetailsContext.Provider value={{ products: orderData?.cartView?.products ?? [], orderStatus: orderData?.status ?? '', mention: orderData?.mention ?? '', doctorAlternativeSuggestions: orderData?.doctorAlternativeSuggestions ?? [] }}>
      <div className="account-content">
      <div className="careplus-order-detail">
        <CarePlusSubscriptionSummary subscription={subscription} language={language} orderHashId={currentDelivery?.hashId} />

        {/* Care Plus orders land here instead of MyAccountOrderDetails; W5 needs a delivery to track */}
        {currentDelivery?.hashId && <AppInstallBanner
          desktopPlacementId="W5"
          mobilePlacementId="W5"
          language={language}
          country={country}
          customerUuid={asideBarData?.uuid}
          isPairedToMobileApp={asideBarData?.isPairedToMobileApp}
          allowedPlacements={appInstallPlacements}
          /* The route param can be the subscription hash, which the app cannot resolve as an order */
          orderHash={currentDelivery?.hashId}
          className="careplus-app-install-banner"
        />}

        {checkpoints.length > 0 && (
          <div className="careplus-order-detail__timeline">
            <Timeline customClass="premium" checkpoints={checkpoints} />
          </div>
        )}

        {/* Pending cancellation request: right under the timeline so the customer sees it first */}
        {pendingCancellation && (
          <div className="careplus-order-detail__pending-cancellation" role="status">
            <p className="careplus-order-detail__pending-cancellation-title">{t('CancellationFlow/pending/title')}</p>
            <p className="careplus-order-detail__pending-cancellation-body">
              {(t(pendingCancellation.type === 'one_time_order' ? 'CancellationFlow/pending/oneTimeBody' : 'CancellationFlow/pending/body') || '')
                // English is the UK: "3 November 2026", not the US order
                .replace(':date', toLocaleDateString(pendingCancellation.cancelAt, language === 'en' ? 'en-GB' : language, dateOptions))}
            </p>
            <button type="button" className="careplus-order-detail__pending-cancellation-keep" disabled={withdrawing} onClick={withdrawCancellationRequest}>
              {t('CancellationFlow/pending/keep')}
            </button>
          </div>
        )}

        {bannersToRender.length > 0 && (
          <div className="careplus-order-detail__notifications">
            {bannersToRender.map((notification, index) => (
              <CarePlusNotificationBanner
                key={`${notification.type}-${notification.data?.deadline ?? notification.data?.renewalDate ?? index}`}
                notification={notification}
                subscriptionID={subscription.id}
                subscriptionHashID={subscription.hashId}
                language={language}
                locale={locale}
                setRefreshData={setRefreshData}
                setStickyNotificationDetails={setStickyNotificationDetails}
                onFocusPaymentMethod={() => setShowPaymentModal(true)}
              />
            ))}
          </div>
        )}

        {/* A first Care+ order can still wait for post-checkout ID / body photos (GB/CH) — same tasks
            and rule as the regular order details page (MyAccountOrderDetails). */}
        {orderData && isPostCheckoutVerificationCountry(country) && (() => {
          const pending = (orderData as any).pendingActions;
          const isVerificationStatus =
            orderData.status === 'wait_customer_additional_actions' || orderData.status === 'id_verification_in_progress';
          const hasBodyPhotos = !!(pending?.frontPhotoDataUrl || pending?.sidePhotoDataUrl);
          const hasPendingTask = !!(
            pending?.needsPhotoUpload || pending?.needsIdVerification || pending?.idVerificationInProgress || pending?.isWaitingActions
          );
          if (!isVerificationStatus && !hasBodyPhotos && !hasPendingTask) return null;
          return (
            <MyAccountPendingActions
              hashID={(orderData as any).hashID ?? currentDelivery?.hashId}
              countryID={country}
              language={language}
              initialPendingData={pending}
            />
          );
        })()}

        {/* Statuses that need the customer: the prescriber asked a question (the message links to the answer page),
            or suggested an alternative treatment ("Show Alternative" opens the accept / deny pop-up) —
            the same status message as the regular order details page (MyAccountOrderDetails). */}
        {orderData && [NEED_CUSTOMER_ANSWER, ALTERNATIVE_TREATMENT_OFFERED].includes(orderData.status) && (() => {
          const statusData = calculateColorAndDescription(orderData, language, 'details');
          if (!statusData.description) return null;
          const alternatives = orderData.doctorAlternativeSuggestions ?? [];
          return (
            <MyAccountOrderDisclaimer
              statusColor={statusData.class}
              description={statusData.description}
              title={statusData.title}
              showIcon={statusData.showIcon}
              cardClass="detail-disclaimer-card"
              actionButton={
                orderData.status === ALTERNATIVE_TREATMENT_OFFERED && alternatives.length > 0 ? (
                  <button
                    className="actionButton"
                    onClick={() => {
                      MainTracker.track('custom', 'My account show doctor alternative', {
                        hashID: orderData.hashID,
                        groupHashID: orderData.groupHashID,
                        status: orderData.status,
                        category: orderData.categoryShortcut,
                        price: alternatives[0]?.originalProductPrice,
                      });
                      setCurrentGlobalModalName({
                        type: 'DOCTOR_ALTERNATIVE_OFFER',
                        orderProducts: (orderData.cartView?.products ?? []).map(() => ({ order_id: orderData.orderID })),
                        doctorAlternativeSuggestions: alternatives,
                        orderBundle: {
                          hashID: orderData.hashID,
                          groupHashID: orderData.groupHashID,
                          orderStatus: orderData.status,
                          categoryShortcut: orderData.categoryShortcut,
                        },
                      });
                    }}
                  >
                    {t('MyOrders/details/alternative_offer_modal/show_alternatives')}
                  </button>
                ) : undefined
              }
            />
          );
        })()}

        {orderData && currentDelivery && (
          <div className="account-order-details">
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/hashID')}
              detailData={currentDelivery.hashId}
              language={language}
            />
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/dateTime')}
              detailData={toLocaleDateString(currentDelivery.orderDate, language, dateOptions)}
              language={language}
            />
            {country?.toLowerCase() === 'gb' && subscription.status === 'active' && subscription.nextRenewalDate && (
              <MyAccountOrderDetailRow
                title={t('CarePlus/detail/nextRenewal')}
                detailData={[
                  toLocaleDateString(subscription.nextRenewalDate, language, dateOptions),
                  subscription.nextRenewalAmount != null ? translator.getPrice(subscription.nextRenewalAmount, locale) : null,
                ].filter(Boolean).join(' · ')}
                language={language}
              />
            )}
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/treatment')}
              detailData={(orderData.cartView?.products ?? []).map((p: any) => ({ image: p.imageSrc, title: p.titleView }))}
              language={language}
              rowID="space-between-row"
            />
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/shippingMethod')}
              detailData={orderData.shippingMethod}
              language={language}
              rowID="space-between-row"
            />
            {orderData.isExternalPharmacy && !EXTERNAL_PHARMACY_INTEGRATIONS_FLOWS.includes(orderData.flow) ? (
              <PharmacySelection orderData={orderData} setOrderData={setOrderData} language={language} />
            ) : orderData.pharmacyDetails && orderData.pharmacyDetails.length > 0 ? (
              <MyAccountOrderDetailRow
                title={t('MyOrders/details/pharmacyDetails')}
                detailData={orderData.pharmacyDetails}
                language={language}
              />
            ) : null}
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/price')}
              detailData={Object.fromEntries(
                Object.entries(currentDelivery.prices)
                  .filter(([, v]) => v != null)
                  // HL-12122: the V3 Monthly plan's row is labelled as the monthly plan, not "Care+ Plan".
                  .map(([k, v]) => [subscription.isMonthlyPlan && k === 'carePlusPlan' ? 'monthlyPlan' : k, translator.getPrice(v as number, locale)])
              )}
              language={language}
            />
            <MyAccountOrderDetailRow
              title={t('MyOrders/details/paymentInfo')}
              detailData=""
              language={language}
              rowID="careplus-payment-row"
            >
              <CarePlusStripePaymentMethod
                subscriptionID={subscription.id}
                mode={hasPaymentFailed ? 'recovery' : 'view'}
                language={language}
                locale={locale}
                onChanged={() => setRefreshData((p: boolean) => !p)}
                setStickyNotificationDetails={setStickyNotificationDetails}
                hideChangeButton={subscription.status === 'cancelled'}
              />
            </MyAccountOrderDetailRow>
            {orderData.address && orderData.address.length > 0 && (
              <MyAccountOrderDetailRow
                title={t('MyOrders/details/address')}
                detailData={orderData.address}
                language={language}
              />
            )}
            {orderData.deliveryAddress && orderData.deliveryAddress.length > 0 && (
              <MyAccountOrderDetailRow
                title={t('MyOrders/details/deliveryAddress')}
                detailData={orderData.deliveryAddress}
                language={language}
              />
            )}
          </div>
        )}

        {deliveryHistory.length > 0 && (
          <div className="careplus-order-detail__section">
            <p className="careplus-order-detail__section-title careplus-order-detail__section-title--lg">{t('CarePlus/detail/deliveryHistory')}</p>
            <CarePlusDeliveryHistory
              items={deliveryHistory}
              country={country}
              language={language}
              isMonthlyPlan={subscription.isMonthlyPlan}
            />
          </div>
        )}

        <div className="careplus-order-detail__actions">
          {subscription.status === 'active' && !pendingCancellation && (
            <button
              type="button"
              className="careplus-order-detail__action-btn careplus-order-detail__action-btn--pause"
              onClick={() => {
                if (useNewCancellationFlow && cancellationFlow?.pause) { setSheetStartStep('pause'); setShowCancellationSheet(true); }
                else setShowPauseModal(true);
              }}
            >
              <img src={PauseIcon} alt="" /> {t('CarePlus/detail/pause')}
            </button>
          )}
          {/* nothing to cancel before the first payment activates the plan */}
          {subscription.status === 'active' && !pendingCancellation && (
            <button
              type="button"
              className="careplus-order-detail__action-btn careplus-order-detail__action-btn--cancel"
              onClick={() => { if (useNewCancellationFlow) { setSheetStartStep('menu'); setShowCancellationSheet(true); } else setShowCancelModal(true); }}
            >
              <img src={CancelIcon} alt="" /> {t('CarePlus/detail/cancel')}
            </button>
          )}
        </div>

        <CarePlusSupport language={language} country={country} />

        <MyAccountOrderNotes
          htmlContent={t('MyOrders/details/orderNotes')}
          language={language}
          orderStatus={currentDelivery?.status ?? ''}
          groupHashID={currentDelivery?.hashId}
          hideCancelOption
        />

        {showPaymentModal && (
          <CarePlusChangePaymentForm
            subscriptionID={subscription.id}
            mode={hasPaymentFailed ? 'recovery' : 'view'}
            language={language}
            onClose={() => setShowPaymentModal(false)}
            onSaved={() => { setShowPaymentModal(false); setRefreshData((p: boolean) => !p); }}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
        {showPauseModal && (
          <CarePlusPauseModal
            subscriptionID={subscription.id}
            subscriptionHashID={subscription.hashId}
            planType={subscription.planType}
            isMonthlyPlan={subscription.isMonthlyPlan}
            language={language}
            onClose={() => setShowPauseModal(false)}
            onSuccess={() => { setShowPauseModal(false); setRefreshData((p: boolean) => !p); }}
            onOpenCancel={() => { setShowPauseModal(false); if (useNewCancellationFlow) { setSheetStartStep('menu'); setShowCancellationSheet(true); } else setShowCancelModal(true); }}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
        {showCancellationSheet && (
          <SubscriptionCancellationSheet
            subscriptionID={subscription.id}
            country={country}
            language={language}
            offerAvailable={Boolean(cancellationFlow?.offer?.available)}
            treatmentWeek={cancellationFlow?.progress?.week ?? 1}
            pauseWindow={cancellationFlow?.pause ?? null}
            isPaused={Boolean(cancellationFlow?.paused)}
            persona={cancellationFlow?.persona ?? 'A'}
            nextChargeDate={cancellationFlow?.nextChargeDate ?? null}
            initialStep={sheetStartStep}
            onClose={() => setShowCancellationSheet(false)}
            onChanged={() => { setShowCancellationSheet(false); setRefreshData((p: boolean) => !p); }}
            onOpenPause={() => { setShowCancellationSheet(false); setShowPauseModal(true); }}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
        {showCancelModal && (
          <CarePlusCancelModal
            subscriptionID={subscription.id}
            subscriptionHashID={subscription.hashId}
            planType={subscription.planType}
            isMonthlyPlan={subscription.isMonthlyPlan}
            language={language}
            country={country}
            isPaused={subscription.status === 'paused'}
            onClose={() => setShowCancelModal(false)}
            onSuccess={() => { setShowCancelModal(false); setRefreshData((p: boolean) => !p); navigate(`/${country}/account/my-orders`); }}
            onOpenPause={() => { setShowCancelModal(false); setShowPauseModal(true); }}
            setStickyNotificationDetails={setStickyNotificationDetails}
          />
        )}
      </div>
      </div>
    </OrderDetailsContext.Provider>
  );
};

export default CarePlusOrderDetail;
